// Minimal promise-based key-value storage. IndexedDB in the browser; the
// in-memory implementation backs unit tests (jsdom has no IndexedDB) and is
// the seam where Capacitor Keystore-backed storage plugs in later (J11).

export interface KvStore {
  get<T>(key: string): Promise<T | undefined>;
  put(key: string, value: unknown): Promise<void>;
  delete(key: string): Promise<void>;
  clear(): Promise<void>;
}

export class MemoryKvStore implements KvStore {
  private readonly map = new Map<string, unknown>();

  get<T>(key: string): Promise<T | undefined> {
    return Promise.resolve(this.map.get(key) as T | undefined);
  }
  put(key: string, value: unknown): Promise<void> {
    this.map.set(key, value);
    return Promise.resolve();
  }
  delete(key: string): Promise<void> {
    this.map.delete(key);
    return Promise.resolve();
  }
  clear(): Promise<void> {
    this.map.clear();
    return Promise.resolve();
  }
}

const DB_NAME = 'merkury';
const STORE_NAME = 'kv';

function requestToPromise<T>(request: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error ?? new Error('IndexedDB request failed'));
  });
}

class IdbKvStore implements KvStore {
  constructor(private readonly db: IDBDatabase) {}

  private store(mode: IDBTransactionMode): IDBObjectStore {
    return this.db.transaction(STORE_NAME, mode).objectStore(STORE_NAME);
  }

  async get<T>(key: string): Promise<T | undefined> {
    return (await requestToPromise(this.store('readonly').get(key))) as T | undefined;
  }
  async put(key: string, value: unknown): Promise<void> {
    await requestToPromise(this.store('readwrite').put(value, key));
  }
  async delete(key: string): Promise<void> {
    await requestToPromise(this.store('readwrite').delete(key));
  }
  async clear(): Promise<void> {
    await requestToPromise(this.store('readwrite').clear());
  }
}

export function openIdbKv(dbName = DB_NAME): Promise<KvStore> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(dbName, 1);
    request.onupgradeneeded = () => {
      if (!request.result.objectStoreNames.contains(STORE_NAME)) {
        request.result.createObjectStore(STORE_NAME);
      }
    };
    request.onsuccess = () => resolve(new IdbKvStore(request.result));
    request.onerror = () => reject(request.error ?? new Error('failed to open IndexedDB'));
  });
}
